import storeAdminTemplate from './storeAdmin.html';
import './App.css';

/*******************************************************************************
 * @ngdoc    component
 * @name     csm.books.StoreAdmin.component:storeAdmin
 * @description
 * Webstore admin application.
 *******************************************************************************/
/* @ngInject */ export function StoreAdminConfig($routeProvider, ROUTES, SERVER) {
  // Load 'No Processors' view if app is not enabled.
  var resolveApp = {
    enabled: ($q, SERVER) =>
      SERVER.enableApp ? $q.resolve() : $q.reject('/np')
  };

  // Show district items only if this org is in a community.
  var resolveCWS = Object.assign(
    {
      showItems: ($q, SERVER) =>
        SERVER.orgInCWS ? $q.resolve() : $q.reject('/')
    },
    resolveApp
  );

  // Show 'No Processors' view only if app is not enabled.
  var resolveNP = {
    enabled: ($q, SERVER) =>
      !SERVER.enableApp ? $q.resolve() : $q.reject('/')
  };

  Object.assign(ROUTES, {
    main: {
      display: 'Store',
      path: '/',
      resolve: resolveApp,
      template: '<ws-store data="$ctrl.data">'
    },
    items: {
      display: 'Items',
      path: '/items/',
      resolve: resolveApp,
      template:
        '<store-items  items="$ctrl.data.store.storeItems" source="$ctrl.data.products">'
    },
    cwsItems: {
      display: 'Communities Items',
      path: '/districtItems/',
      resolve: resolveCWS,
      template:
        '<store-items  items="$ctrl.data.store.districtItems" source="$ctrl.data.products">'
    },
    policies: {
      display: 'Policies',
      path: '/policies/',
      resolve: resolveApp,
      template:
        '<ws-policies store="$ctrl.data.store" processors="$ctrl.data.processors" show-cf="$ctrl.showCF">'
    },
    np: {
      path: '/np',
      resolve: resolveNP,
      template: '<ws-no-proc></ws-no-proc>',
      disableApp: true
    }
  });

  Object.values(ROUTES)
    /**
     * Filter out the items route if the district items are disabled. This way the option still appears but if the user
     * forcefully tries to navigate (via changing url) nothing will happen.
     */
    .filter(
      (r) => !(r.path === ROUTES.items.path && SERVER.disableDistrictItems)
    )
    .forEach((r) => $routeProvider.when(r.path, r));
  $routeProvider.otherwise(ROUTES.main.path);
}

export const storeAdminComponent = {
  controller: StoreAdminController,
  template: storeAdminTemplate
}

/*---------------------------------------*/
/* Redirect routes that fail to resolve. */
/*---------------------------------------*/
/* @ngInject */ export function HandleRouteChangeError($rootScope, $location) {
  $rootScope.$on('$routeChangeError', (e, c, p, path) =>
    $location.url(path)
  );
}

/*******************************************************************************
 * controller
 *******************************************************************************/
/* @ngInject */ function StoreAdminController(
  $injector,
  $location,
  $rootScope,
  $scope,
  BusyScreen,
  StoreAdminService,
  SERVER,
  ROUTES
) {
  var $ctrl = this;
  var $svcs = { $ctrl: $ctrl, $scope: $scope };
  $ctrl.enableApp = SERVER.enableApp;
  $ctrl.showCF = !SERVER.orgHasCWS;
  $ctrl.onOption = $injector.invoke(onOption, null, $svcs);
  $ctrl.save = $injector.invoke(save, null, $svcs);

  /*-----------------------------*/
  /* Get the initial information */
  /*-----------------------------*/
  BusyScreen.promise(
    StoreAdminService.load().then((data) => {
      $ctrl.data = data;

      $ctrl.data.communityStoreLink = $ctrl.data.communityLink
        ? `${window.document.location.protocol}//${$ctrl.data.communityLink}`
        : null;

      $ctrl.data.showFundsPurchase = SERVER.productID === 'ASB Works';
      /*-----------------------------------------*/
      /* Set up the available navigation options */
      /*-----------------------------------------*/
      $ctrl.options = [ROUTES.main, ROUTES.items, ROUTES.policies];
      if (SERVER.orgInCWS) $ctrl.options.splice(2, 0, ROUTES.cwsItems);

      /*---------------------------------------------------------------*/
      /* Set the active option on load and whenever the route changes. */
      /*---------------------------------------------------------------*/
      const setActiveOption = () =>
        ($ctrl.activeOption = $ctrl.options.find(
          (o) => o.path == $location.path()
        ));
      setActiveOption();
      $rootScope.$on('$routeChangeSuccess', setActiveOption);
    })
  );
}

/*****************************************************************************
 * $ctrl.onOption
 *
 * Loads the tab options and sets the current location to active.
 *****************************************************************************/
/* @ngInject */ function onOption($ctrl, $location, Alert, ROUTES, SERVER) {
  return function (option) {
    if (!option) return $ctrl.activeOption;

    // Refer to server store admin controller comment for details.
    if (SERVER.disableDistrictItems && ROUTES.items.path === option.path) {
      Alert.info(
        'You do not have an active processor! No items can be added until you have setup an active processor for this district!',
        'CWS Restriction'
      );
      return $ctrl.activeOption;
    }

    $location.path(option.path);
  };
}

/*****************************************************************************
 * $ctrl.save
 *
 * Called on the Save Changes button. Saves the store setting and the items.
 *****************************************************************************/
/* @ngInject */ function save(
  $ctrl,
  Alert,
  BusyScreen,
  DomainNameCheck,
  StoreAdminService,
  ROUTES,
  SERVER
) {
  return function () {
    Alert.clear();

    var hasError = false;

    /*-----------------------------------------------------------------*/
    /* Set the convenience fee amounts to zero if nothing was entered. */
    /*-----------------------------------------------------------------*/
    $ctrl.data.store.config.convenienceFeePercent =
      Number($ctrl.data.store.config.convenienceFeePercent) || 0;
    $ctrl.data.store.config.convenienceFeeFixed =
      Number($ctrl.data.store.config.convenienceFeeFixed) || 0;

    /*-------------------------------------*/
    /* Check the convenience fee percent.  */
    /* Can not have a value of more than 1 */
    /*-------------------------------------*/
    if ($ctrl.data.store.config.convenienceFeePercent > 1) {
      Alert.error('Amount can not be more than 1.0000', 'Sale Percentage');
      hasError = true;
    }

    /*-----------------------*/
    /* Check the domain name */
    /*-----------------------*/
    var errorMessage = DomainNameCheck.check(
      $ctrl.data.store.siteDomain,
      true
    );
    if (errorMessage) {
      Alert.error(errorMessage, 'Domain Name');
      hasError = true;
    }

    /***
     * For District with KPS we allow the district to have no processors so we can
     * make it so our clients can use CWS as a hub for their clients w/o having
     * a district account/processor they need to sell to. Normally we do not allow
     * our school to not have a processor for the store but this is the only exception.
     */
    const isDistrictWithCnpKps = $ctrl.data.kpsActive && SERVER.orgHasCWS;

    /***
    * Now check if hte processor ID is empty if it is do not allow the customer to continue,
    * unless the this is a district with CWS and KPS.
    */
    if (!(Number($ctrl.data.store.config.processorID) || 0) && !isDistrictWithCnpKps) {
      Alert.error('Value required', 'Checkout Payment Method');
      hasError = true;
    }

    /*------------------------------------*/
    /* If any of the above checks failed, */
    /* return the user to the Store tab.  */
    /*------------------------------------*/
    if (hasError) return $ctrl.onOption(ROUTES.main);

    /*---------------------------------------*/
    /* Save store settings, and store items. */
    /*---------------------------------------*/
    BusyScreen.promise(
      StoreAdminService.save($ctrl.data.store)
        .then(function () {
          Alert.success('Store saved');
        })
        .catch(function (r) {
          Alert.error(r.parsedResponse.message(), ' ');
        })
    );
  };
}
